import React from 'react';
import HiresPage from '../components/hires/HiresPage';
import { readInitialHiresView } from '../constants/hiresViewTabs';

/**
 * DEPARTMENT HIRES — every hire line of the ACTIVE department.
 *
 * 🔑 **This file is a SCOPE DECLARATION, not a page.** The page body — views,
 * toolbar, search, actions, dialogs, table — is `components/hires/HiresPage.js`,
 * shared with `MyHires.js` and `AdminHires.js` (owner, 2026-10-03: *"lets do
 * them all"*). Everything below is a difference this page really has from the
 * other two. Read `HiresPage.js`'s docblock for the rules governing all three.
 *
 * 🔻 `const deptName = activeDepartment?.name || 'Department';` WAS DELETED HERE
 *    ON 2026-08-12, AND IT WAS DEAD CODE, NOT A BROKEN HEADING. It carried the
 *    same wrong-key defect as the rest of the file (an assignment row has
 *    `department_name`, never `name`) but was also never rendered. **If this
 *    page ever wants a heading, that is a new feature with a real element, not
 *    the restoration of that line.**
 */
export const DEPARTMENT_HIRES_SCOPE = Object.freeze({
  logLabel: 'DepartmentHires',
  testId: 'department-hires-page',
  rootClassName: 'p-2.5 max-md:pt-0 max-w-full',
  contextLabel: 'All hires',
  actionsHeaderTitle: 'Department Hires',
  loadingMessage: 'Loading department hire data...',
  emptyTitle: 'No hires in this department yet',
  emptyDescription: 'Hire items appear here as soon as an order with hire equipment is approved.',

  /** An explicit saved choice wins; otherwise TABLE on desktop, KANBAN below 768px. */
  initialView: readInitialHiresView,

  /** A click on a hire CARD opens the HIRE in the Table (owner, 2026-09-27). */
  openHireFromCard: true,

  /**
   * 🔴 TRUE — this page always shows `requested_by`, so the Table is always the
   * FULL `HIRE_TABLE_COLUMNS` / `HIRE_TABLE_COL_WIDTHS_KEY` (and below `md` the
   * full phone set and ITS key), never the `MY_HIRE_*` subset.
   */
  showRequestedBy: true,

  /** Department Hires keeps the phone money figures (owner, 2026-10-01). */
  showPhoneSummary: true,

  /** The `CostSummary` banner is dropped on a PHONE in every view, as on My Hires
   *  (owner ruling 2026-10-03); in Table view its figures still ride the
   *  status-tab line (`CostSummaryInline`, owner 2026-09-27). */
  hideCostBannerOnPhone: true,

  hideGanttOnPhone: true,

  // ── Behaviour flags, each this page's shipped behaviour ────────────────
  poll: true,
  staleRequestGuard: true,

  /**
   * 🔴 TRUE — and this page is the ONLY one of the three that has it.
   * `hooks/useSubmitGuard.js` (2026-08-13): `modalLoading` is state set BEFORE
   * the await, which is the fast-double-click hole on a Confirm button; the
   * guard's REF drops the concurrent duplicate synchronously. It fronts the
   * three modal handlers and the Gantt drag — the same PATCH family, which the
   * modal serialises anyway.
   */
  submitGuard: true,

  /**
   * 🔴 TRUE — F8 (silent-failure survey), and this page is the ONLY one that
   * carries it. A refused CALENDAR response used to be swallowed (only hires
   * and summary were allowed to throw), so the page rendered fully healthy with
   * `error` null and an empty off-hire/return calendar. On a surface whose whole
   * job is deadlines, an empty calendar reads as *"nothing is due back"* — a
   * confident false claim, not an absence of one.
   */
  throwOnCalendarError: true,

  holdRowAfterAction: true,

  alwaysQueryString: true,

  /**
   * 🚨 `department_id`, NEVER `id` — AN ASSIGNMENT ROW HAS NO `id` AND THIS READ
   *    `activeDepartment?.id` UNTIL 2026-08-12. `activeDepartment` is always an
   *    element of the array `GET /api/roles/my-assignments` returns, and that
   *    route selects `da.department_id`, `d.name AS department_name`, … — there
   *    is no `id` in the projection. So the guard below was permanently false,
   *    the parameter was NEVER SENT, and the server fell back to
   *    `buildDeptFilter`'s `o.department_id IN (<every department the caller
   *    belongs to>)`: this page listed the hires of every department the viewer
   *    is in, on a page whose entire premise is that it shows one.
   *    Pinned by `DepartmentHires.departmentScope.test.js` and, for the SHAPE
   *    rather than this instance, by
   *    `contexts/activeDepartmentKeys.manifest.test.js`.
   */
  buildQueryString: ({ activeDepartment }) => {
    const activeDepartmentId = activeDepartment?.department_id ?? null;
    const params = new URLSearchParams();
    if (activeDepartmentId != null) params.set('department_id', activeDepartmentId);
    return params.toString();
  },

  resolveFetchKey: ({ activeDepartment }) => activeDepartment?.department_id ?? null,

  /**
   * 🔑 FAIL CLOSED: "no department selected" must not read as "every
   *    department". That is exactly what the 2026-08-12 defect produced — an
   *    absent parameter, which the server answers with the caller's whole
   *    membership — so the absence of a scope is a refusal to ask, not a
   *    wildcard. The shared body then renders the explicit "No department
   *    selected" card, because *"this department has no hires"* and *"no
   *    department is selected"* are different claims.
   *
   * ⚠️ `isAdmin` IS THE ONE EXEMPTION AND IT IS NOT AN OVERSIGHT. A global admin
   *    holds NO department assignments: `GET /api/roles/my-assignments` returns
   *    `assignments: []` and `DepartmentContext` sets `activeDepartment` to
   *    `null` on that branch BY DESIGN, so "no active department" means
   *    something completely different for an admin. `App.js`'s
   *    `DepartmentRoute` admits `isAdmin` to this page explicitly, and the
   *    server scopes their request through `buildDeptFilter`'s own
   *    `req.isGlobalAdmin` short-circuit. Failing closed on an admin would
   *    blank the page for the one caller for whom the empty state means
   *    nothing. Pinned by `DepartmentHires.adminNoActiveDept.test.js`.
   */
  resolveCanQuery: ({ isAdmin, activeDepartment }) => isAdmin || (activeDepartment?.department_id ?? null) != null,

  /**
   * 🚨 WAS `isAdmin || isBuyer` — the UNSCOPED `user_roles` row
   *    (`server/middleware/departmentScope.js`), which has nothing to do with
   *    the department these hires belong to and no `isSupervisor` disjunct at
   *    all. It DENIED a correctly-invited department buyer with no unscoped
   *    row, and it would have GRANTED an unscoped buyer standing in a
   *    department they hold no seat in at all.
   *
   * `capabilitiesFor(activeDepartment?.department_id)` matches the server's own
   * question EXACTLY here: every item on this page comes from
   * `department_id=<activeDepartmentId>` and the server's `buildDeptFilter` is
   * itself single-department scoped, so every item on screen genuinely belongs
   * to the active department. The five mutation routes
   * (extend/shorten/change-start/off-hire/re-hire) all gate on
   * `requireDepartmentCapability(req, res, item.order_department_id,
   * 'canManageDepartmentOrders')` — the same capability, asked about the same
   * department.
   */
  resolveCanManage: ({ isAdmin, activeDepartment, capabilitiesFor }) => isAdmin || Boolean(
    capabilitiesFor(activeDepartment?.department_id).canManageDepartmentOrders
  ),

  /** Always the Department Orders panel — that IS this page's surface. */
  resolveDeptWide: () => true,

  /**
   * Deep-links into the DEPARTMENT ORDERS list with the row auto-expanded,
   * rather than `/orders/:id`'s separate page. Owner: *"Clicking on the hire in
   * dep hires takes us to a different page for the order than if we are in dep
   * orders. Have a look and see if we can just go to the dep order page and
   * expand that hire order."* Closes his own 2026-08-19 `/orders/:id`-retirement
   * ruling: *"Deep links → the order list with that row auto-expanded."*
   * `DepartmentOrders.js`'s `?order=` resolution effect does the rest.
   * `/orders/:id` itself is NOT retired or deleted by this.
   */
  viewOrderPath: (orderId) => `/department/orders?order=${orderId}`,

  /** Cross-tab refresh is scoped to THIS department's events. */
  resolveInvalidationDepartmentId: ({ activeDepartment }) => activeDepartment?.department_id ?? null,

  resolveNotifyDepartmentId: ({ activeDepartment }) => activeDepartment?.department_id ?? null,
});

function DepartmentHires() {
  return <HiresPage scope={DEPARTMENT_HIRES_SCOPE} />;
}

export default DepartmentHires;
